import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, ChevronLeft, ChevronRight, Copy, CheckCircle2, Clock, Repeat } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const PLATFORMS = {
  facebook:   { label: "Facebook",    color: "bg-blue-100 text-blue-700 border-blue-200",    dot: "bg-blue-500" },
  tiktok:     { label: "TikTok",      color: "bg-slate-800 text-white border-slate-700",     dot: "bg-slate-800" },
  instagram:  { label: "Instagram",   color: "bg-pink-100 text-pink-700 border-pink-200",    dot: "bg-pink-500" },
  marketplace:{ label: "Marketplace", color: "bg-emerald-100 text-emerald-700 border-emerald-200", dot: "bg-emerald-500" },
  "2ememain": { label: "2ememain",    color: "bg-orange-100 text-orange-700 border-orange-200", dot: "bg-orange-500" },
};

const CRENEAUX = {
  facebook:   "Mar-Jeu 9h-12h",
  tiktok:     "Mar-Ven 18h-21h",
  instagram:  "Lun-Ven 11h-13h",
  marketplace:"Quotidien 8h-9h / 19h-20h",
  "2ememain": "Quotidien 7h-8h",
};

const STATUT_STYLE = {
  brouillon: "bg-slate-100 text-slate-600",
  programme: "bg-indigo-100 text-indigo-700",
  publie:    "bg-emerald-100 text-emerald-700",
};

const getMonday = (d) => {
  const date = new Date(d);
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(date.setDate(diff));
};

const fmtDate = (d) => d.toISOString().slice(0, 10);
const JOURS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

const Social = () => {
  const { token } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [recurDialogOpen, setRecurDialogOpen] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [weekStart, setWeekStart] = useState(getMonday(new Date()));
  const [form, setForm] = useState({ texte: "", plateforme: "facebook", date_prevue: "", heure_prevue: "", statut: "brouillon", recurrent: false, notes: "" });
  const [recurForm, setRecurForm] = useState({ texte: "", plateforme: "marketplace", heure_prevue: "08:00", days: 7 });

  const headers = { Authorization: `Bearer ${token}` };

  const fetchPosts = async () => {
    try {
      const res = await axios.get(`${API}/social/posts?semaine=${fmtDate(weekStart)}`, { headers });
      setPosts(res.data);
    } catch { toast.error("Erreur chargement"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchPosts(); }, [weekStart]);

  const weekDays = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(weekStart);
      d.setDate(d.getDate() + i);
      return { date: fmtDate(d), label: JOURS[i], num: d.getDate(), month: d.toLocaleDateString("fr-FR", { month: "short" }) };
    });
  }, [weekStart]);

  const postsByDay = useMemo(() => {
    const map = {};
    weekDays.forEach(d => { map[d.date] = []; });
    posts.forEach(p => { if (map[p.date_prevue]) map[p.date_prevue].push(p); });
    return map;
  }, [posts, weekDays]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPost) {
        await axios.put(`${API}/social/posts/${editingPost.id}`, form, { headers });
        toast.success("Post modifié !");
      } else {
        await axios.post(`${API}/social/posts`, form, { headers });
        toast.success("Post créé !");
      }
      closeDialog();
      fetchPosts();
    } catch { toast.error("Erreur"); }
  };

  const handleRecurring = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API}/social/generate-recurring`, {
        ...recurForm, start_date: fmtDate(weekStart),
      }, { headers });
      toast.success(res.data.message);
      setRecurDialogOpen(false);
      fetchPosts();
    } catch { toast.error("Erreur"); }
  };

  const closeDialog = () => { setDialogOpen(false); setEditingPost(null); };

  const openNew = (date, plateforme) => {
    setEditingPost(null);
    const suggested = CRENEAUX[plateforme]?.match(/\d+h/)?.[0]?.replace("h", ":00") || "";
    setForm({ texte: "", plateforme: plateforme || "facebook", date_prevue: date || "", heure_prevue: suggested, statut: "brouillon", recurrent: false, notes: "" });
    setDialogOpen(true);
  };

  const openEdit = (p) => {
    setEditingPost(p);
    setForm({ texte: p.texte, plateforme: p.plateforme, date_prevue: p.date_prevue, heure_prevue: p.heure_prevue || "", statut: p.statut, recurrent: p.recurrent || false, notes: p.notes || "" });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    try { await axios.delete(`${API}/social/posts/${id}`, { headers }); fetchPosts(); }
    catch { toast.error("Erreur"); }
  };

  const handleStatusToggle = async (p) => {
    const next = p.statut === "brouillon" ? "programme" : p.statut === "programme" ? "publie" : "brouillon";
    try { await axios.put(`${API}/social/posts/${p.id}`, { statut: next }, { headers }); fetchPosts(); }
    catch { toast.error("Erreur"); }
  };

  const prevWeek = () => { const d = new Date(weekStart); d.setDate(d.getDate() - 7); setWeekStart(d); };
  const nextWeek = () => { const d = new Date(weekStart); d.setDate(d.getDate() + 7); setWeekStart(d); };
  const thisWeek = () => setWeekStart(getMonday(new Date()));

  const today = fmtDate(new Date());
  const totalWeek = posts.length;
  const publies = posts.filter(p => p.statut === "publie").length;
  const programmes = posts.filter(p => p.statut === "programme").length;

  return (
    <div className="space-y-5 animate-fade-in" data-testid="social-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-800">Réseaux Sociaux</h1>
          <p className="text-slate-500 mt-1">Calendrier éditorial — planifie tes posts</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setRecurDialogOpen(true)} data-testid="recurring-btn">
            <Repeat className="w-4 h-4 mr-2" />Récurrent
          </Button>
          <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={() => openNew(today, "facebook")} data-testid="add-post-social-btn">
            <Plus className="w-4 h-4 mr-2" />Nouveau post
          </Button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card><CardContent className="p-3"><p className="text-xs text-slate-500">Cette semaine</p><p className="text-xl font-bold text-slate-800">{totalWeek}</p></CardContent></Card>
        <Card><CardContent className="p-3"><p className="text-xs text-slate-500">Programmés</p><p className="text-xl font-bold text-indigo-600">{programmes}</p></CardContent></Card>
        <Card><CardContent className="p-3"><p className="text-xs text-slate-500">Publiés</p><p className="text-xl font-bold text-emerald-600">{publies}</p></CardContent></Card>
        <Card><CardContent className="p-3"><p className="text-xs text-slate-500">Brouillons</p><p className="text-xl font-bold text-slate-500">{totalWeek - publies - programmes}</p></CardContent></Card>
      </div>

      {/* Créneaux optimaux */}
      <Card>
        <CardContent className="p-3">
          <p className="text-xs font-semibold text-slate-500 mb-2">CRÉNEAUX OPTIMAUX (BELGIQUE)</p>
          <div className="flex flex-wrap gap-2">
            {Object.entries(PLATFORMS).map(([k, v]) => (
              <div key={k} className={`text-xs px-2 py-1 rounded-full border ${v.color}`}>
                {v.label}: {CRENEAUX[k]}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Week navigation */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={prevWeek}><ChevronLeft className="w-4 h-4" /></Button>
        <div className="flex items-center gap-2">
          <span className="font-medium text-slate-700">
            {weekDays[0]?.num} {weekDays[0]?.month} — {weekDays[6]?.num} {weekDays[6]?.month}
          </span>
          <Button variant="outline" size="sm" onClick={thisWeek}>Aujourd'hui</Button>
        </div>
        <Button variant="ghost" size="sm" onClick={nextWeek}><ChevronRight className="w-4 h-4" /></Button>
      </div>

      {/* Calendar grid */}
      {loading ? (
        <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div></div>
      ) : (
        <div className="grid grid-cols-7 gap-2" data-testid="calendar-grid">
          {weekDays.map(day => {
            const dayPosts = postsByDay[day.date] || [];
            const isToday = day.date === today;
            // Check missing daily platforms
            const dailyPlatforms = ["marketplace", "2ememain"];
            const missingDaily = dailyPlatforms.filter(dp => !dayPosts.some(p => p.plateforme === dp));

            return (
              <Card key={day.date} className={`min-h-[180px] ${isToday ? "border-indigo-400 bg-indigo-50/30" : ""}`} data-testid={`day-${day.date}`}>
                <CardContent className="p-2">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <span className={`text-xs font-bold ${isToday ? "text-indigo-600" : "text-slate-500"}`}>{day.label}</span>
                      <span className={`text-sm ml-1 font-medium ${isToday ? "text-indigo-700" : "text-slate-700"}`}>{day.num}</span>
                    </div>
                    <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => openNew(day.date, "facebook")}>
                      <Plus className="w-3 h-3" />
                    </Button>
                  </div>

                  <div className="space-y-1.5">
                    {dayPosts.map(p => {
                      const plat = PLATFORMS[p.plateforme] || PLATFORMS.facebook;
                      return (
                        <div key={p.id} className={`p-1.5 rounded border text-xs group cursor-pointer ${plat.color}`}
                          onClick={() => openEdit(p)} data-testid={`post-${p.id}`}>
                          <div className="flex items-center justify-between">
                            <span className="font-medium truncate">{plat.label}</span>
                            <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button onClick={(e) => { e.stopPropagation(); handleStatusToggle(p); }} className="hover:scale-110" title="Changer statut">
                                {p.statut === "publie" ? <CheckCircle2 className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                              </button>
                              <button onClick={(e) => { e.stopPropagation(); handleDelete(p.id); }} className="hover:text-red-600" title="Supprimer">
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </div>
                          </div>
                          {p.heure_prevue && <span className="opacity-70">{p.heure_prevue}</span>}
                          <p className="truncate mt-0.5">{p.texte}</p>
                          {p.recurrent && <Repeat className="w-3 h-3 opacity-50 inline" />}
                        </div>
                      );
                    })}

                    {/* Missing daily alerts */}
                    {missingDaily.map(mp => (
                      <button key={mp} onClick={() => openNew(day.date, mp)}
                        className="w-full p-1 rounded border border-dashed border-red-200 text-xs text-red-400 hover:bg-red-50 text-center">
                        + {PLATFORMS[mp]?.label}
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Post dialog */}
      <Dialog open={dialogOpen} onOpenChange={(o) => { if (!o) closeDialog(); else setDialogOpen(o); }}>
        <DialogContent data-testid="social-post-dialog">
          <DialogHeader><DialogTitle>{editingPost ? "Modifier le post" : "Nouveau post"}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-3 mt-2">
            <div className="space-y-1">
              <Label>Texte du post</Label>
              <textarea value={form.texte} onChange={e => setForm({ ...form, texte: e.target.value })}
                className="w-full min-h-[80px] p-3 border rounded-md text-sm" placeholder="Écris ton post ici..." required data-testid="social-texte" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Plateforme</Label>
                <Select value={form.plateforme} onValueChange={v => setForm({ ...form, plateforme: v })}>
                  <SelectTrigger data-testid="social-plateforme"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(PLATFORMS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Statut</Label>
                <Select value={form.statut} onValueChange={v => setForm({ ...form, statut: v })}>
                  <SelectTrigger data-testid="social-statut"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="brouillon">Brouillon</SelectItem>
                    <SelectItem value="programme">Programmé</SelectItem>
                    <SelectItem value="publie">Publié</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Date</Label><Input type="date" value={form.date_prevue} onChange={e => setForm({ ...form, date_prevue: e.target.value })} required data-testid="social-date" /></div>
              <div className="space-y-1">
                <Label>Heure <span className="text-slate-400 text-xs">({CRENEAUX[form.plateforme]})</span></Label>
                <Input type="time" value={form.heure_prevue} onChange={e => setForm({ ...form, heure_prevue: e.target.value })} data-testid="social-heure" />
              </div>
            </div>
            <div className="space-y-1"><Label>Notes</Label><Input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Notes libres..." data-testid="social-notes" /></div>
            <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700" data-testid="submit-social-btn">{editingPost ? "Enregistrer" : "Créer"}</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Recurring dialog */}
      <Dialog open={recurDialogOpen} onOpenChange={setRecurDialogOpen}>
        <DialogContent data-testid="recurring-dialog">
          <DialogHeader><DialogTitle>Créer des posts récurrents</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">Génère automatiquement un post par jour pour Marketplace ou 2ememain</p>
          <form onSubmit={handleRecurring} className="space-y-3 mt-2">
            <div className="space-y-1">
              <Label>Texte du post modèle</Label>
              <textarea value={recurForm.texte} onChange={e => setRecurForm({ ...recurForm, texte: e.target.value })}
                className="w-full min-h-[60px] p-3 border rounded-md text-sm" placeholder="Texte qui sera dupliqué chaque jour..." required data-testid="recur-texte" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label>Plateforme</Label>
                <Select value={recurForm.plateforme} onValueChange={v => setRecurForm({ ...recurForm, plateforme: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="marketplace">Marketplace</SelectItem>
                    <SelectItem value="2ememain">2ememain</SelectItem>
                    <SelectItem value="facebook">Facebook</SelectItem>
                    <SelectItem value="tiktok">TikTok</SelectItem>
                    <SelectItem value="instagram">Instagram</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1"><Label>Heure</Label><Input type="time" value={recurForm.heure_prevue} onChange={e => setRecurForm({ ...recurForm, heure_prevue: e.target.value })} /></div>
              <div className="space-y-1"><Label>Jours</Label><Input type="number" min="1" max="30" value={recurForm.days} onChange={e => setRecurForm({ ...recurForm, days: parseInt(e.target.value) || 7 })} /></div>
            </div>
            <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700" data-testid="submit-recur-btn">Générer {recurForm.days} posts</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Social;
